import React from 'react';
import { useTranslation } from 'react-i18next';

interface ExcelPreviewTableProps {
  sheetData: any[];
  sheetName?: string;
  showHeaders?: boolean;
}

const ExcelPreviewTable: React.FC<ExcelPreviewTableProps> = ({
  sheetData,
  sheetName,
  showHeaders = true
}) => {
  const { t } = useTranslation();
  // Determine if the first row should be treated as headers
  const hasHeaders = showHeaders && sheetData.length > 0;
  const headerRow = hasHeaders ? sheetData[0] : null;
  const dataRows = hasHeaders && headerRow ? sheetData.slice(1) : sheetData;

  if (sheetData.length === 0) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <p className="text-gray-500 dark:text-gray-400">{t('No data to display')}</p>
      </div>
    );
  }

  return (
    <div className="w-full bg-white dark:bg-gray-900">
      {/* Sheet name indicator (optional) */}
      {sheetName && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('Sheet:')} <span className="font-semibold">{sheetName}</span>
          </p>
        </div>
      )}

      {/* Table container - scrolling handled by parent */}
      <div className="bg-white dark:bg-gray-900">
        <table className="border-collapse text-sm">
          {/* Header row */}
          {hasHeaders && headerRow && (
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-800 border-b border-gray-300 dark:border-gray-600">
                {Array.isArray(headerRow) ? (
                  headerRow.map((cell, cellIdx) => (
                    <th
                      key={cellIdx}
                      className={`border-r border-gray-200 dark:border-gray-700 px-4 py-3 text-left font-bold text-gray-900 dark:text-gray-100 whitespace-nowrap min-w-max ${
                        cellIdx === 0
                          ? 'sticky left-0 bg-gray-100 dark:bg-gray-800 z-10'
                          : ''
                      }`}
                    >
                      {cell}
                    </th>
                  ))
                ) : (
                  <th className="sticky left-0 bg-gray-100 dark:bg-gray-800 z-10 border-r border-gray-200 dark:border-gray-700 px-4 py-3 text-left font-bold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                    {JSON.stringify(headerRow)}
                  </th>
                )}
              </tr>
            </thead>
          )}

          {/* Data rows */}
          <tbody>
            {dataRows.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className={`border-b border-gray-200 dark:border-gray-700 ${
                  rowIdx % 2 === 0
                    ? 'bg-white dark:bg-gray-900'
                    : 'bg-gray-50 dark:bg-gray-800'
                }`}
              >
                {Array.isArray(row) ? (
                  row.map((cell, cellIdx) => (
                    <td
                      key={cellIdx}
                      className={`border-r border-gray-200 dark:border-gray-700 px-4 py-2 text-gray-900 dark:text-gray-100 whitespace-nowrap min-w-max ${
                        cellIdx === 0
                          ? 'sticky left-0 font-bold bg-gray-50 dark:bg-gray-800 z-10'
                          : ''
                      }`}
                    >
                      {cell}
                    </td>
                  ))
                ) : (
                  <td className="sticky left-0 font-bold bg-gray-50 dark:bg-gray-800 z-10 border-r border-gray-200 dark:border-gray-700 px-4 py-2 text-gray-900 dark:text-gray-100 whitespace-nowrap">
                    {JSON.stringify(row)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExcelPreviewTable;
